import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canAccess, ROLE_CONFIG } from "../utils/roles";

function Header() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "";
  const rc = ROLE_CONFIG[role] || {
    color: "#6b7280",
    bg: "#f9fafb",
    label: role,
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const letterheadOpen = pathname.startsWith("/letterhead");
  const vendorOpen = pathname.startsWith("/vendor");
  const expenseOpen =
    pathname.startsWith("/expense") || pathname.startsWith("/etams");
  const projectAdminOpen = pathname.startsWith("/project-admin");
  const requestsOpen = pathname.startsWith("/requests");
  const itOpen = pathname.startsWith("/it-facilities");
  const deptOpen = ["/engineering", "/procurement", "/operations", "/hr-admin", "/ehs"].some((p) =>
    pathname.startsWith(p),
  );
  const financeOpen = pathname.startsWith("/finance") || pathname.startsWith("/cashbook");
  const adminOpen = pathname.startsWith("/admin/console") || pathname.startsWith("/admin/workflows");

  // Renders a collapsible sidebar group with exactly the same markup/classes
  // as the existing Letterhead / Travel Expense / Vendor groups.
  const renderGroup = (id, icon, label, items, isOpen) =>
    items.length > 0 && (
      <li className="nav-item" key={id}>
        <a
          className={`nav-link ${isOpen ? "" : "collapsed"}`}
          href="#"
          data-bs-toggle="collapse"
          data-bs-target={`#${id}`}
          aria-expanded={isOpen}
        >
          <i className={`nav-icon bi ${icon} me-2`} />
          {label}
        </a>
        <div id={id} className={`collapse ${isOpen ? "show" : ""}`}>
          <ul className="nav flex-column">
            {items.map((item) => (
              <li key={item.to} className="nav-item">
                <NavLink to={item.to} end className="nav-link">
                  <i className={`bi ${item.icon} me-1`} />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </li>
    );

  const allowed = (items) => items.filter((i) => canAccess(user, i.key));

  const projectAdminItems = allowed([
    { key: "projectadmin.letters", to: "/project-admin/letters", icon: "bi-envelope-paper", label: "Project Letters" },
    { key: "projectadmin.codes", to: "/project-admin/project-codes", icon: "bi-upc-scan", label: "Project Codes" },
    { key: "projectadmin.costcenters", to: "/project-admin/cost-centers", icon: "bi-diagram-3", label: "Cost Centers" },
  ]);
  const requestItems = allowed([
    { key: "workspace.requests", to: "/requests/memos", icon: "bi-file-earmark-check", label: "Approval Memos" },
    { key: "workspace.requests", to: "/requests/my", icon: "bi-list-check", label: "My Requests" },
  ]);
  const itItems = allowed([
    { key: "it.asset", to: "/it-facilities/it-asset", icon: "bi-laptop", label: "IT Asset Request" },
    { key: "it.desk", to: "/it-facilities/desk", icon: "bi-building", label: "Desk & Office Space" },
  ]);
  const deptItems = allowed([
    { key: "engineering.docs", to: "/engineering", icon: "bi-rulers", label: "Engineering" },
    { key: "procurement.stores", to: "/procurement", icon: "bi-cart3", label: "Procurement" },
    { key: "operations.site", to: "/operations", icon: "bi-cone-striped", label: "Operations" },
    { key: "hradmin.services", to: "/hr-admin", icon: "bi-people", label: "HR & Admin" },
    { key: "ehs.compliance", to: "/ehs", icon: "bi-shield-check", label: "EHS & Compliance" },
  ]);
  const financeItems = allowed([
    { key: "finance.control", to: "/finance", icon: "bi-bank", label: "Finance & Payments" },
    { key: "finance.cashbook", to: "/cashbook", icon: "bi-journal-bookmark", label: "Cashbook" },
  ]);
  const adminItems = allowed([
    { key: "admin.console", to: "/admin/console", icon: "bi-shield-lock", label: "Users & Access" },
    { key: "admin.workflows", to: "/admin/workflows", icon: "bi-diagram-2", label: "Workflows & Integrations" },
  ]);

  // Count accessible vendor items to decide whether to show vendor section at all
  const vendorItems = [
    {
      key: "vendor.master",
      to: "/vendor/master",
      icon: "bi-person-badge",
      label: "Vendor Master",
    },
    {
      key: "vendor.projects",
      to: "/vendor/projects",
      icon: "bi-kanban",
      label: "Project Codes",
    },
    {
      key: "vendor.payment_requests",
      to: "/vendor/payment-requests",
      icon: "bi-clipboard-plus",
      label: "Payment Requests",
    },
    {
      key: "vendor.invoice_upload",
      to: "/vendor/invoice-upload",
      icon: "bi-cloud-upload",
      label: "Invoice Upload",
    },
    {
      key: "vendor.grn",
      to: "/vendor/grn-verification",
      icon: "bi-box-seam",
      label: "GRN Verification",
    },
    {
      key: "vendor.approvals",
      to: "/vendor/approvals",
      icon: "bi-person-check",
      label: "L3 Approvals",
    },
    {
      key: "vendor.vendor_approvals",
      to: "/vendor/vendor-approvals",
      icon: "bi-patch-check",
      label: "Vendor Approvals",
    },
    {
      key: "vendor.finance",
      to: "/vendor/finance",
      icon: "bi-bank",
      label: "Finance Processing",
    },
    {
      key: "vendor.released",
      to: "/vendor/payment-released",
      icon: "bi-send-check",
      label: "Payment Released",
    },
    {
      key: "vendor.reports",
      to: "/vendor/reports",
      icon: "bi-bar-chart",
      label: "Reports",
    },
  ].filter((i) => canAccess(user, i.key));

  const expenseItems = [
    {
      key: "etams.overview",
      to: "/etams/overview",
      icon: "bi-house",
      label: "Overview",
    },
    {
      key: "etams.travel",
      to: "/etams/travel-requests",
      icon: "bi-airplane",
      label: "Travel Requests",
    },
    {
      key: "etams.travel",
      to: "/etams/travel-requests/new",
      icon: "bi-file-earmark-plus",
      label: "New Travel Request",
    },
    {
      key: "expense.advance",
      to: "/expense/advance-request",
      icon: "bi-wallet2",
      label: "Travel Advances",
    },
    {
      key: "expense.claim",
      to: "/expense/claim-submission",
      icon: "bi-receipt",
      label: "Expense Reports",
    },
    {
      key: "expense.claim",
      to: "/etams/claims/new",
      icon: "bi-plus-circle",
      label: "Create Expense Claim",
    },
    {
      key: "etams.receipts",
      to: "/etams/receipts",
      icon: "bi-paperclip",
      label: "Receipt Centre",
    },
    {
      key: "expense.approvals",
      to: "/expense/approvals",
      icon: "bi-person-check",
      label: "Approvals",
    },
    {
      key: "expense.finance",
      to: "/expense/finance-review",
      icon: "bi-bank",
      label: "Settlements",
    },
    {
      key: "etams.policy",
      to: "/etams/policy",
      icon: "bi-book",
      label: "Travel Policy",
    },
    {
      key: "expense.reports",
      to: "/expense/reports",
      icon: "bi-bar-chart",
      label: "Reports",
    },
  ].filter((i) => canAccess(user, i.key));

  const letterheadItems = [
    {
      key: "letterhead.templates",
      to: "/letterhead/templates",
      icon: "bi-grid-3x2-gap",
      label: "Templates",
    },
    {
      key: "letterhead.editor",
      to: "/letterhead/editor",
      icon: "bi-pencil-square",
      label: "Create / Edit",
    },
    {
      key: "letterhead.approvals",
      to: "/letterhead/approvals",
      icon: "bi-check2-circle",
      label: "Approval Flow",
    },
    {
      key: "letterhead.history",
      to: "/letterhead/history",
      icon: "bi-clock-history",
      label: "History",
    },
  ].filter((i) => canAccess(user, i.key));

  return (
    <nav className="navbar-vertical navbar overflow-y-scroll">
      <div className="vh-100 d-flex flex-column justify-content-between">
        <div>
          <div
            style={{
              padding: "14px 16px 8px",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <div class="card bg-dark-success shadow-none text-center mb-2 mt-1 border-0 rounded-4">
              <div class="card-body py-5">
                <h5 class="text-white m-0 lh-1">TSE Workspace</h5>
                <p class="text-white fs-6 lh-md fw-normal">
                  Integrated Management System
                </p>
                {user && (
                  <p className="text-white lh-sm fs-5 mt-3 mb-0">
                    {user.userName}
                    <br />
                    {user.department}
                  </p>
                )}
              </div>
            </div>
          </div>

          <ul className="navbar-nav flex-column pt-2" id="sideNavbar">
            <li className="nav-item">
              <NavLink to="/dashboard" className="nav-link">
                <i className="nav-icon bi bi-speedometer2 me-2" />
                Dashboard
              </NavLink>
            </li>

            {/* Letterhead Section */}
            {letterheadItems.length > 0 && (
              <>
                <li className="nav-item">
                  <a
                    className={`nav-link ${letterheadOpen ? "" : "collapsed"}`}
                    href="#"
                    data-bs-toggle="collapse"
                    data-bs-target="#navLetterhead"
                    aria-expanded={letterheadOpen}
                  >
                    <i className="nav-icon bi bi-file-earmark-text me-2" />
                    Letterhead
                  </a>
                  <div
                    id="navLetterhead"
                    className={`collapse ${letterheadOpen ? "show" : ""}`}
                  >
                    <ul className="nav flex-column">
                      {letterheadItems.map((item) => (
                        <li key={item.key} className="nav-item">
                          <NavLink to={item.to} className="nav-link">
                            <i className={`bi ${item.icon} me-1`} />
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              </>
            )}

            {/* Travel Expense Section */}
            {expenseItems.length > 0 && (
              <>
                <li className="nav-item">
                  <a
                    className={`nav-link ${expenseOpen ? "" : "collapsed"}`}
                    href="#"
                    data-bs-toggle="collapse"
                    data-bs-target="#navExpense"
                    aria-expanded={expenseOpen}
                  >
                    <i className="nav-icon bi bi-airplane me-2" />
                    eTAMS
                  </a>
                  <div
                    id="navExpense"
                    className={`collapse ${expenseOpen ? "show" : ""}`}
                  >
                    <ul className="nav flex-column">
                      {expenseItems.map((item) => (
                        <li key={item.to} className="nav-item">
                          <NavLink to={item.to} end className="nav-link">
                            <i className={`bi ${item.icon} me-1`} />
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              </>
            )}

            {/* Vendor Section */}
            {vendorItems.length > 0 && (
              <>
                <li className="nav-item">
                  <a
                    className={`nav-link ${vendorOpen ? "" : "collapsed"}`}
                    href="#"
                    data-bs-toggle="collapse"
                    data-bs-target="#navVendor"
                    aria-expanded={vendorOpen}
                  >
                    <i className="nav-icon bi bi-shop me-2" />
                    Vendor Payment
                  </a>
                  <div
                    id="navVendor"
                    className={`collapse ${vendorOpen ? "show" : ""}`}
                  >
                    <ul className="nav flex-column">
                      {vendorItems.map((item) => (
                        <li key={item.key} className="nav-item">
                          <NavLink to={item.to} className="nav-link">
                            <i className={`bi ${item.icon} me-1`} />
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              </>
            )}

            {canAccess(user, "businesscard.request") && (
              <li className="nav-item">
                <NavLink to="/business-card/requests" className="nav-link">
                  <i className="nav-icon bi bi-person-vcard me-2" />
                  Business Card
                </NavLink>
              </li>
            )}

            {canAccess(user, "complaints.box") && (
              <li className="nav-item">
                <NavLink to="/complaints" className="nav-link">
                  <i className="nav-icon bi bi-megaphone me-2" />
                  Complaint Box
                </NavLink>
              </li>
            )}

            {/* ── TSE Workspace ─────────────────────────────────────── */}
            {canAccess(user, "workspace.wall") && (
              <li className="nav-item">
                <NavLink to="/employee-wall" className="nav-link">
                  <i className="nav-icon bi bi-chat-square-text me-2" />
                  Employee Wall
                </NavLink>
              </li>
            )}
            {canAccess(user, "workspace.profile") && (
              <li className="nav-item">
                <NavLink to="/profile" className="nav-link">
                  <i className="nav-icon bi bi-person me-2" />
                  My Profile
                </NavLink>
              </li>
            )}
            {canAccess(user, "workspace.approvals") && (
              <li className="nav-item">
                <NavLink to="/approvals" className="nav-link">
                  <i className="nav-icon bi bi-check2-square me-2" />
                  My Approvals
                </NavLink>
              </li>
            )}
            {canAccess(user, "workspace.servicehub") && (
              <li className="nav-item">
                <NavLink to="/service-hub" className="nav-link">
                  <i className="nav-icon bi bi-headset me-2" />
                  Service Hub
                </NavLink>
              </li>
            )}
            {renderGroup("navProjectAdmin", "bi-folder2-open", "Project Admin", projectAdminItems, projectAdminOpen)}
            {renderGroup("navRequests", "bi-file-earmark-check", "Requests", requestItems, requestsOpen)}
            {renderGroup("navIT", "bi-pc-display", "IT & Facilities", itItems, itOpen)}
            {renderGroup("navDept", "bi-grid", "Departments", deptItems, deptOpen)}
            {renderGroup("navFinance", "bi-cash-coin", "Finance", financeItems, financeOpen)}
            {canAccess(user, "workspace.policies") && (
              <li className="nav-item">
                <NavLink to="/policies" className="nav-link">
                  <i className="nav-icon bi bi-journal-text me-2" />
                  Policy Center
                </NavLink>
              </li>
            )}
            {renderGroup("navAdminConsole", "bi-gear", "Admin Console", adminItems, adminOpen)}

            {/* Administration Section (Admin only) */}
            {canAccess(user, "admin.users") && (
              <li className="nav-item">
                <NavLink to="/admin/users" className="nav-link">
                  <i className="nav-icon bi bi-people-fill me-2" />
                  User Management
                </NavLink>
              </li>
            )}
          </ul>
        </div>

        <div className="mx-3 mt-6">
          <button
            onClick={handleLogout}
            className="btn btn-danger rounded-3 fw-normal w-100"
          >
            Logout
          </button>
        </div>
        <div className="p-2 mt-1"></div>
      </div>
    </nav>
  );
}
export default Header;
